import React, { useEffect } from 'react';
import { Modal, Empty, List, Card, Badge, Spin, Alert } from 'antd';
import { BsHash } from 'react-icons/bs';
import { LoadingOutlined } from '@ant-design/icons';
import { useGetStoryHashtags } from '@coreStory/useCase';
import type { IStoryHashtagResponse } from '@coreStory/type';
import styles from './index.module.scss';

interface HashtagModalProps {
  visible: boolean;
  onCancel: () => void;
  storyId: number | null;
  storyName?: string;
}

const HashtagModal: React.FC<HashtagModalProps> = ({
  visible,
  onCancel,
  storyId,
  storyName,
}) => {
  const {
    mutate: getHashtags,
    data: hashtagData,
    isLoading,
    error,
  } = useGetStoryHashtags();

  useEffect(() => {
    if (visible && storyId) {
      getHashtags({ story_id: storyId });
    }
  }, [visible, storyId, getHashtags]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            size="large"
          />
          <p>Loading hashtags...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          message="Error"
          description="Failed to load hashtags. Please try again."
          type="error"
          showIcon
          className={styles.errorAlert}
        />
      );
    }

    if (!hashtagData || hashtagData.length === 0) {
      return (
        <div className={styles.emptyContainer}>
          <Empty
            image={<BsHash size={48} className={styles.emptyIcon} />}
            description={
              <div className={styles.emptyDescription}>
                <h3>No Hashtags Available</h3>
                <p>This story doesn&apos;t have any hashtags yet.</p>
              </div>
            }
          />
        </div>
      );
    }

    return (
      <div className={styles.hashtagContent}>
        <div className={styles.hashtagHeader}>
          <div className={styles.iconContainer}>
            <BsHash size={24} className={styles.hashtagIcon} />
          </div>
          <h3>Story Hashtags</h3>
          {/* <Badge count={hashtagData.length} className={styles.totalBadge} /> */}
        </div>

        <div className={styles.hashtagList}>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 1,
              md: 2,
              lg: 2,
              xl: 2,
              xxl: 2,
            }}
            dataSource={hashtagData}
            renderItem={(hashtag: IStoryHashtagResponse) => (
              <List.Item>
                <Card
                  hoverable
                  className={styles.hashtagCard}
                  bodyStyle={{ padding: '16px 20px' }}>
                  <div className={styles.hashtagItem}>
                    <div className={styles.hashtagInfo}>
                      <div className={styles.hashtagName}>
                        <BsHash size={16} className={styles.hashIcon} />
                        <span className={styles.tagText}>
                          {hashtag.Hashtag}
                        </span>
                      </div>
                      {/* <div className={styles.hashtagMeta}>
                        <span className={styles.hashtagId}>
                          ID: {hashtag.HashtagID}
                        </span>
                      </div> */}
                    </div>
                    <div className={styles.usageCount}>
                      <Badge
                        count={hashtag.UsedNumbers}
                        className={styles.usageBadge}
                        style={{
                          backgroundColor: '#52c41a',
                        }}
                      />
                      {/* <span className={styles.usageLabel}>Uses</span> */}
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      className={styles.hashtagModal}
      centered>
      {renderContent()}
    </Modal>
  );
};

export default HashtagModal;
